import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardBody, CardHeader, CardTitle } from './ui/Card';
import { CardListToolbar } from './ui/CardListToolbar';
import { Button } from './ui/Button';
import { AlertMessage } from './ui/AlertMessage';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { Badge } from './ui/Badge';
import { OrderDetail } from './orders/OrderDetail';
import { canonicalStatus, statusLabel, statusMeta, TONE_BG } from './orders/workflowConstants';
import {
  API_BASE_URL,
} from './orders/orderFormHelpers';
import { AuthenticatedImage } from './ui/AuthenticatedImage';
import {
  applyOrderListFilters,
  countActiveOrderFilters,
  DEFAULT_ORDER_LIST_FILTERS,
  readOrderStatusFromSearch,
  parseOrdersRoute,
  ordersNewPath,
  ordersDetailPath,
} from './orders/orderListFilterUtils';
import { OrderListFiltersPanel } from './orders/OrderListFiltersPanel';
import { pathForMenu } from '../routes/menuPaths';
import { useModuleAccess } from '../permissions/useModuleAccess';
import { useCommercialAccess } from '../permissions/useCommercialAccess';
import { ModuleAccessBanner } from './ui/ModuleAccessBanner';

/**
 * Orders list. One click on a card opens the unified OrderDetail page which
 * handles create, edit and view in a single surface. The old 3-step add/edit
 * wizard and the separate preview modal are gone — see OrderDetail for that
 * logic.
 */
function Orders() {
  const { canCreate, canEdit } = useModuleAccess('ORDERS');
  const canSeeCommercial = useCommercialAccess();
  const canCreateOrder = canCreate && canSeeCommercial;
  const location = useLocation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [pageMessage, setPageMessage] = useState('');

  /** Derived from URL: list | new | detail */
  const ordersRoute = useMemo(
    () => parseOrdersRoute(location.pathname),
    [location.pathname]
  );

  const [pendingSelectDesignId, setPendingSelectDesignId] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [orderFilters, setOrderFilters] = useState(() => {
    const status = readOrderStatusFromSearch(window.location.search);
    if (status) {
      return { ...DEFAULT_ORDER_LIST_FILTERS, orderStatus: status };
    }
    return { ...DEFAULT_ORDER_LIST_FILTERS };
  });
  const [filtersOpen, setFiltersOpen] = useState(() => Boolean(readOrderStatusFromSearch(window.location.search)));

  useEffect(() => {
    const status = readOrderStatusFromSearch(location.search);
    if (!status) return;
    setOrderFilters((prev) => ({ ...DEFAULT_ORDER_LIST_FILTERS, ...prev, orderStatus: status }));
    setFiltersOpen(true);
    navigate(pathForMenu('Orders'), { replace: true });
  }, [location.search, navigate]);

  const goToOrdersList = useCallback(() => {
    setPendingSelectDesignId(null);
    navigate({ pathname: '/orders', search: location.search });
  }, [navigate, location.search]);

  const openNewOrder = useCallback(() => {
    navigate(ordersNewPath());
  }, [navigate]);

  const openOrderDetail = useCallback((orderId) => {
    navigate(ordersDetailPath(orderId));
  }, [navigate]);

  useEffect(() => {
    const selectDesignId = location.state?.selectDesignId;
    if (selectDesignId == null || selectDesignId === '') return;
    setPendingSelectDesignId(selectDesignId);
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: {} }
    );
  }, [location.state?.selectDesignId, location.pathname, location.search, navigate]);

  /** Legacy dashboard links that still pass openOrderId in location.state. */
  useEffect(() => {
    const openId = location.state?.openOrderId;
    if (openId == null || openId === '') return;
    const selectDesignId = location.state?.selectDesignId;
    navigate(
      {
        pathname: ordersDetailPath(openId),
        search: location.search,
        state: selectDesignId ? { selectDesignId } : undefined,
      },
      { replace: true }
    );
  }, [location.state?.openOrderId, location.state?.selectDesignId, location.search, navigate]);

  const headers = useMemo(() => {
    const token = localStorage.getItem('token');
    const h = { 'Content-Type': 'application/json' };
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, []);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setPageError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/orders`, { headers });
      if (!res.ok) throw new Error('Unable to load orders');
      const data = await res.json();
      setOrders(Array.isArray(data) ? data : []);
    } catch (e) {
      setPageError(e?.message || 'Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    if (ordersRoute.screen !== 'list') return;
    loadOrders();
  }, [ordersRoute.screen, loadOrders]);

  useEffect(() => {
    if (!pageMessage) return undefined;
    const t = setTimeout(() => setPageMessage(''), 3500);
    return () => clearTimeout(t);
  }, [pageMessage]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleteLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/orders/${confirmDelete.id}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) throw new Error('Unable to delete order');
      setOrders((prev) => prev.filter((o) => o.id !== confirmDelete.id));
      setPageMessage('Order deleted');
      setConfirmDelete(null);
    } catch (e) {
      setPageError(e?.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  const filterBadge = countActiveOrderFilters(orderFilters);

  const visibleOrders = useMemo(() => {
    const filtered = applyOrderListFilters(orders, orderFilters, { includeCommercial: canSeeCommercial });
    return [...filtered].sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      if (tb !== ta) return tb - ta;
      return String(b.id ?? '').localeCompare(String(a.id ?? ''));
    });
  }, [orders, orderFilters, canSeeCommercial]);

  // Redirect unauthorized users away from /orders/new
  useEffect(() => {
    if (ordersRoute.screen === 'new' && !canCreateOrder) {
      goToOrdersList();
    }
  }, [ordersRoute.screen, canCreateOrder, goToOrdersList]);

  const showOrderDetail = ordersRoute.screen === 'new' || ordersRoute.screen === 'detail';
  const detailOrderId = ordersRoute.screen === 'detail' ? ordersRoute.orderId : null;

  if (showOrderDetail) {
    if (ordersRoute.screen === 'new' && !canCreateOrder) {
      return null;
    }
    return (
      <div className="mx-auto flex min-h-0 flex-1 w-full max-w-[1500px] flex-col">
        <OrderDetail
          key={ordersRoute.screen === 'new' ? 'new' : detailOrderId}
          orderId={detailOrderId}
          initialSelectDesignId={pendingSelectDesignId}
          onBack={goToOrdersList}
          onDraftCreated={(id) => navigate(ordersDetailPath(id), { replace: true })}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1500px] space-y-3">
      <ModuleAccessBanner moduleKey="ORDERS" />
      <Card>
        <CardHeader variant="gradient" className="!py-2.5 sm:!py-3">
          <CardListToolbar
            tone="light"
            left={
              <CardTitle className="!text-base !text-white sm:!text-lg">Orders</CardTitle>
            }
            actions={
              <>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setFiltersOpen((open) => !open)}
                  className="w-full shrink-0 sm:w-auto"
                >
                  <svg
                    className="mr-1.5 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                    />
                  </svg>
                  Filters
                  {filterBadge > 0 && (
                    <span className="ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary-600 px-1.5 text-[10px] font-bold text-white">
                      {filterBadge}
                    </span>
                  )}
                </Button>
                {canCreateOrder && (
                  <Button type="button" size="sm" onClick={openNewOrder} className="w-full shrink-0 sm:w-auto">
                    <svg className="mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Order
                  </Button>
                )}
              </>
            }
          />
        </CardHeader>
        <CardBody className="space-y-3 !p-3 sm:!p-4">
          <AlertMessage type="success" message={pageMessage} onClose={() => setPageMessage('')} />
          <AlertMessage type="error" message={pageError} onClose={() => setPageError('')} />

          {filtersOpen ? (
            <OrderListFiltersPanel
              value={orderFilters}
              onApply={setOrderFilters}
              orders={orders}
              showCommercial={canSeeCommercial}
            />
          ) : null}

          {loading ? (
            <p className="py-10 text-center text-sm text-gray-500 sm:text-base">Loading orders…</p>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {visibleOrders.map((o) => (
                <OrderCard
                  key={o.id}
                  order={o}
                  onOpen={() => openOrderDetail(o.id)}
                  onDelete={canEdit && canSeeCommercial ? () => setConfirmDelete(o) : undefined}
                />
              ))}
            </div>
          )}

          {!loading && orders.length === 0 && (
            <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/60 px-6 py-16 text-center">
              <p className="mx-auto max-w-md text-base text-gray-600 sm:text-lg">No orders yet.</p>
              <p className="mx-auto mt-2 max-w-md text-sm text-gray-500 sm:text-base">
                {canCreateOrder
                  ? (
                    <>
                      Click <span className="font-semibold">Add Order</span> to create your first one.
                    </>
                  )
                  : 'You can view orders here when they are created.'}
              </p>
            </div>
          )}

          {!loading && orders.length > 0 && visibleOrders.length === 0 && (
            <p className="py-8 text-center text-sm text-slate-500">No orders match your filters.</p>
          )}
        </CardBody>
      </Card>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Delete order?"
        message={confirmDelete ? `Remove ${confirmDelete.orderCode || 'this order'} permanently?` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleteLoading}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}

/** Statuses where the order has entered shop-floor / post-production. */
const PRODUCTION_STARTED_STATUSES = new Set([
  'IN_PRODUCTION',
  'PRODUCTION_DONE',
  'BUTTA_CUTTING',
  'MILL_FINISH',
  'BORDER_CUTTING',
  'HANDWORK',
  'READY_FOR_DISPATCH',
  'IN_STOCK',
  'DISPATCHED',
  'DELIVERED',
]);

function formatCardDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatOrderCode(code) {
  const raw = String(code || '').trim();
  if (!raw) return '—';
  return raw.startsWith('#') ? raw : `#${raw}`;
}

/** Clickable card — layout mirrors Designs list cards. */
function OrderCard({ order, onOpen, onDelete }) {
  const canonical = canonicalStatus(order.workflowStatus);
  const tone = statusMeta(order.workflowStatus).tone;
  const orderCode = order.orderCode || order.id;
  const chalanNo = String(order.chalanNo || '').trim();
  const showProductionUnit =
    Boolean(String(order.khataName || '').trim()) && PRODUCTION_STARTED_STATUSES.has(canonical);
  const pcsText =
    order.totalPieces != null ? `${Number(order.totalPieces).toLocaleString()} Pcs` : '—';

  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  };

  const iconBtn =
    'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200/80 bg-white shadow-sm transition-colors';

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={handleKey}
      className="group cursor-pointer overflow-hidden rounded-xl border border-slate-200 bg-white shadow-md ring-1 ring-slate-900/[0.04] transition-all hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40"
    >
      <div className="flex items-end justify-between gap-2 border-b border-slate-100 px-3 py-2">
        <div className="min-w-0 flex-1 pr-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Order number</p>
          <p className="truncate font-mono text-sm font-bold tabular-nums text-slate-900" title={String(orderCode)}>
            {formatOrderCode(orderCode)}
          </p>
          <p className="mt-0.5 text-xs text-slate-600">
            <span className="font-semibold text-slate-700">Order date</span>{' '}
            <time
              dateTime={order.orderDate ? String(order.orderDate).slice(0, 10) : undefined}
              className="tabular-nums text-slate-800"
            >
              {formatCardDate(order.orderDate)}
            </time>
          </p>
        </div>
        {onDelete ? (
          <button
            type="button"
            title="Delete"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className={`${iconBtn} text-rose-600 hover:bg-rose-50 hover:border-rose-200`}
            aria-label={`Delete order ${orderCode}`}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        ) : null}
      </div>

      <div className="flex gap-3 p-3">
        <div className="h-36 w-28 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-100 sm:h-40 sm:w-32">
          {order.designImage ? (
            <AuthenticatedImage
              src={order.designImage}
              alt={order.designSampleNo ? `Design ${order.designSampleNo}` : 'Design image'}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center px-1 text-center text-[10px] leading-tight text-slate-500">
              No image
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-2 text-[13px] leading-snug">
          <div>
            <p className="text-[11px] font-medium text-slate-500">Design no.</p>
            <p className="truncate font-mono text-sm font-bold tabular-nums text-slate-900" title={order.designSampleNo || ''}>
              {order.designSampleNo ? `#${order.designSampleNo}` : '—'}
            </p>
          </div>
          <p className="text-xs text-slate-600">
            <span className="font-semibold text-slate-700">Pcs =</span>{' '}
            <span className="text-slate-800">{pcsText}</span>
          </p>
          {chalanNo ? (
            <p className="text-xs text-slate-600">
              <span className="font-semibold text-slate-700">Chalan no. =</span>{' '}
              <span className="text-slate-800">{chalanNo}</span>
            </p>
          ) : null}
          {showProductionUnit ? (
            <p className="text-xs text-slate-600">
              <span className="font-semibold text-slate-700">Production unit =</span>{' '}
              <span className="text-slate-800">{order.khataName}</span>
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 border-t border-slate-100 bg-slate-50/60 px-3 py-2">
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-bold ring-1 ${
            TONE_BG[tone] || TONE_BG.slate
          }`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-current opacity-75" />
          {statusLabel(canonical)}
        </span>
        {order.onHold && (
          <Badge tone="warning" dot>
            On hold
          </Badge>
        )}
        <span className="ml-auto text-[11px] text-slate-500">
          <span className="font-semibold text-slate-600">Status updated</span>{' '}
          <time
            dateTime={order.updatedAt ? String(order.updatedAt).slice(0, 10) : undefined}
            className="tabular-nums text-slate-700"
          >
            {formatCardDate(order.updatedAt)}
          </time>
        </span>
      </div>
    </article>
  );
}

export default Orders;
